import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { ConfirmationService, MessageService, SortEvent } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { ActivatedRoute, Router } from '@angular/router';
import { MovimentoConsultaDTO } from '../../../../../models/movimentacao.dto';
import { MovimentacaoService } from '../../../../../services/financeiro/movimentacao.service';
import { ItemsService } from '../../../../../services/financeiro/item.service';
import { ItemDTO } from '../../../../../models/item.model';

@Component({
  selector: 'app-pessoa-items-table',
  templateUrl: './items-table.component.html',
  styleUrls: ['./items-table.component.scss'],
  providers: [ConfirmationService, MessageService],
})
export class ItemsTableComponent implements OnInit, OnDestroy {
  @Input() pessoaId: number | undefined;
  items: ItemDTO[] = [] as ItemDTO[];
  first = 0;
  rows = 5;
  totalRecords = 0;
  lastfield = '';
  lastSort = '';
  searchCodigo: string = '';
  options = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 120, value: 120 },
  ];
  tipoDeTransacao = [
    { label: 'Todas', value: 'DESPESA,RECEITA' },
    { label: 'Receita', value: 'RECEITA' },
    { label: 'Despesa', value: 'DESPESA' },
  ];
  transacaoSelecionada = { label: 'todas', value: 'DESPESA,RECEITA' };
  rangeDates: any[] = [];

  // Objetos de traducao
  headers = {
    imagem: '',
    nome: '',
    codigo_item: '',
    codigo_barras: '',
    custo: '',
    preco_venda: '',
    saldo: '',
    estoque_minimo: '',
    tipo_imposto: '',
    tipo_desconto: '',
    desconto: '',
    data_criacao: ''
  };

  texts = {
    itens_por_pagina: ''
  };

  private langChangeSub!: Subscription;

  constructor(
    private router: Router,
    private itemsService: ItemsService,
    private translate: TranslateService
  ) {}

  async ngOnInit() {
    this.loadTranslations();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });

    if (!this.pessoaId) {
      return;
    }
    await this.findMovimentacao(0, this.rows, 'id,asc');
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  private loadTranslations(): void {
    this.headers = {
      imagem: this.translate.instant('shared.pessoas-items-table.headers.imagem'),
      nome: this.translate.instant('shared.pessoas-items-table.headers.nome'),
      codigo_item: this.translate.instant('shared.pessoas-items-table.headers.codigo_item'),
      codigo_barras: this.translate.instant('shared.pessoas-items-table.headers.codigo_barras'),
      custo: this.translate.instant('shared.pessoas-items-table.headers.custo'),
      preco_venda: this.translate.instant('shared.pessoas-items-table.headers.preco_venda'),
      saldo: this.translate.instant('shared.pessoas-items-table.headers.saldo'),
      estoque_minimo: this.translate.instant('shared.pessoas-items-table.headers.estoque_minimo'),
      tipo_imposto: this.translate.instant('shared.pessoas-items-table.headers.tipo_imposto'),
      tipo_desconto: this.translate.instant('shared.pessoas-items-table.headers.tipo_desconto'),
      desconto: this.translate.instant('shared.pessoas-items-table.headers.desconto'),
      data_criacao: this.translate.instant('shared.pessoas-items-table.headers.data_criacao')
    };

    this.texts = {
      itens_por_pagina: this.translate.instant('shared.pessoas-items-table.itens_por_pagina')
    };
  }
  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.search();
    }
  }

  search() {
    this.findMovimentacao(0, this.rows, `${this.lastfield},${this.lastSort}`);
  }

  findMovimentacao(
    page: number = 0,
    size: number = this.rows,
    sort: string = 'id,asc'
  ): Promise<void> {
    return new Promise((resolve) => {
      const codigoMovimento = this.searchCodigo
        ? `&nome=${this.searchCodigo}`
        : '';

      const filter = `fornecedorPrincipal=${this.pessoaId}${codigoMovimento}`;

      this.itemsService
        .get('/item/buscarPorFornecedorPrincipal', { page, size, sort, filter })
        .subscribe(
          (response) => {
            this.items = response.content;
            this.totalRecords = response.totalElements;
            this.first = page * size;
            this.rangeDates = [];
            resolve();
          },
          (error) => {
            console.error('Erro ao buscar items:', error);
            this.items = [];
            this.totalRecords = 0;
            this.rangeDates = [];
          }
        );
    });
  }

  onPageChange(event: any) {
    const page = event.page; // Página atual a partir do paginator
    const size = event.rows; // Quantidade de itens por página
    this.findMovimentacao(page, size);
  }

  onRowsPerPageChange() {
    this.first = 0; // Reiniciar a página para o início
    this.findMovimentacao(0, this.rows); // Buscar a primeira página com o novo tamanho
  }

  customSort(event: SortEvent) {
    let sort = 'desc';
    if (event.order === 1) {
      sort = 'asc';
    } // 1 é ascendente, -1 é descendente}

    if (this.lastfield === event.field && this.lastSort === sort) {
      return;
    } // Essencial para não cair em loop infinito

    this.lastfield = String(event.field);
    this.lastSort = String(sort);

    this.findMovimentacao(0, this.rows, `${event.field},${sort}`);
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0'); // Preenche com zero à esquerda
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Meses começam em 0
    const year = String(date.getFullYear()).slice(-4); // Pega os últimos 2 dígitos do ano
    return `${year}-${month}-${day}`;
  }
}
